import { useState } from "react";
import { createPortal } from "react-dom";
import { X, Check } from "lucide-react";
import axios from "axios";
import { toast } from "react-toastify";
import { useAuth } from "./AuthContext";
import { cn } from "../../lib/cn";
import { AI_MODEL_OPTIONS, resolveAiModel } from "../../constants/aiModels";
import { useBodyScrollLock } from "../../hooks/useBodyScrollLock";

export default function AiModelModal({
  isOpen,
  onClose,
  aiModel,
  setAiModel,
}) {
  const [saving, setSaving] = useState(false);
  const { isAuthenticated } = useAuth();

  useBodyScrollLock(isOpen);

  if (!isOpen) return null;

  const resolvedAiModel = resolveAiModel(aiModel);

  const handleSelect = async (newModel) => {
    if (newModel === resolvedAiModel) {
      onClose();
      return;
    }

    setAiModel(newModel);
    setSaving(true);

    if (!isAuthenticated) {
      toast.success("AI model updated for this session");
      setSaving(false);
      onClose();
      return;
    }

    try {
      const token = localStorage.getItem("token");
      const res = await axios.post(
        `${import.meta.env.VITE_BACKEND_URL}/api/auth/profile/update-ai-model`,
        { aiModel: newModel },
        { headers: { Authorization: `Bearer ${token}` } },
      );
      if (res.data?.message === "AI model updated successfully.") {
        toast.success("AI model saved to your account");
      } else {
        toast.error("Failed to save AI model to your account");
      }
    } catch {
      toast.error("Failed to save AI model to your account");
    } finally {
      setSaving(false);
      onClose();
    }
  };

  return createPortal(
    <div
      className="fixed inset-0 z-50 overflow-hidden"
      onClick={onClose}
      role="presentation"
    >
      <div className="absolute inset-0 bg-black/50" aria-hidden />
      <div className="relative z-10 flex h-full items-center justify-center p-4 overflow-y-auto">
        <div
          className="bg-card rounded-xl border border-border shadow-lg w-full max-w-md max-h-[90vh] flex flex-col"
          onClick={(e) => e.stopPropagation()}
          role="dialog"
          aria-modal="true"
          aria-labelledby="ai-model-modal-title"
        >
        <div className="flex items-center justify-between px-5 pt-5 pb-3 border-b border-border">
          <div>
            <h2
              id="ai-model-modal-title"
              className="text-lg font-semibold text-foreground"
            >
              Choose AI model
            </h2>
            <p className="mt-0.5 text-xs text-muted-foreground">
              For AI usage across PageTest
            </p>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="rounded-md p-1.5 text-muted-foreground hover:text-foreground hover:bg-muted transition-colors"
            aria-label="Close"
          >
            <X size={20} />
          </button>
        </div>

        <ul className="p-3 space-y-1 overflow-y-auto">
          {AI_MODEL_OPTIONS.map((option) => {
            const selected = resolvedAiModel === option.value;
            return (
              <li key={option.value}>
                <button
                  type="button"
                  disabled={saving}
                  onClick={() => handleSelect(option.value)}
                  className={cn(
                    "w-full flex items-center gap-3 rounded-lg border px-3 py-2.5 text-left transition-colors",
                    "disabled:opacity-60 disabled:pointer-events-none",
                    selected
                      ? "border-brand bg-brand/5"
                      : "border-transparent hover:border-border hover:bg-muted/50",
                  )}
                >
                  <span
                    className={cn(
                      "flex h-5 w-5 shrink-0 items-center justify-center rounded-full border",
                      selected
                        ? "border-brand bg-brand text-white"
                        : "border-border bg-background",
                    )}
                  >
                    {selected && <Check size={12} strokeWidth={3} />}
                  </span>
                  <span className="min-w-0 flex-1">
                    <span className="block text-sm font-medium text-foreground">
                      {option.label}
                    </span>
                    <span className="block text-xs text-muted-foreground">
                      {option.hint}
                    </span>
                  </span>
                </button>
              </li>
            );
          })}
        </ul>

        <div className="px-5 py-3 border-t border-border">
          <button
            type="button"
            onClick={onClose}
            className="w-full rounded-lg border border-border px-4 py-2 text-sm font-medium text-foreground hover:bg-muted transition-colors"
          >
            Cancel
          </button>
        </div>
        </div>
      </div>
    </div>,
    document.body,
  );
}
